/**
 * A viewer's refused changes (decision 4 of
 * docs/plans/2026-10-02-crdt-schema-v2.md): changes made on this device that
 * the server will not store, because this user may now only view the map.
 *
 * - Made while connected (in flight when the role changed: seconds of work):
 *   dropped without asking — the map is reloaded from the server.
 * - Some made while disconnected, or left by an earlier session (possibly
 *   hours of work): "N changes could not be saved" with "Save them as a
 *   copy" (a new map of this user's, as this device had it), "Discard them",
 *   or "Later" (kept on this device, asked again when the map is opened).
 *
 * Only for the open map; a linked map's refused changes stay on the device.
 * The sync channel reports them (websocket.js onChangesRefused, the bridge's
 * CHANGES_REFUSED once the role is known).
 */
import { on, Events } from "../core/events.js";
import { deleteDocCache } from "../storage/index.js";
import { markConfirmed } from "./unsynced-changes.js";
import { ask, notify } from "../host.js";
import { NOTICE, QUESTION } from "../notices.js";
import { t } from "../../shared/i18n/index.js";

let cb = null;
let unsubscribe = null;
const handling = new Set(); // docIds being handled now
const later = new Set(); // "Later" chosen, or a copy failed: not asked again this session
const discarded = new Set(); // reloaded once this session: a second refusal is asked, never looped

/**
 * @param {{
 *   getCurrentDocId: () => string|null,
 *   getMapName: (docId: string) => string,
 *   stopSync: () => Promise<void>,
 *   reopen: (docId: string) => Promise<void>,
 *   copyMap: (docId: string, options: {title: string}) => Promise<unknown>,
 * }} callbacks
 */
export function initRefusedChanges(callbacks) {
  cb = callbacks;
  unsubscribe?.();
  unsubscribe = on(Events.CHANGES_REFUSED, ({ docId, offline, count }) => {
    void handleRefusedChanges(docId, { offline, count });
  });
}

async function handleRefusedChanges(docId, { offline, count }) {
  if (!cb || !docId || handling.has(docId) || later.has(docId)) return;
  if (cb.getCurrentDocId() !== docId) return;
  handling.add(docId);
  try {
    if (!offline && !discarded.has(docId)) {
      console.log("[RefusedChanges] Changes refused while online (now a viewer): reloading the map", docId);
      await discardLocalChanges(docId);
      return;
    }
    const choice = await ask({ code: QUESTION.REFUSED_CHANGES, data: { mapName: cb.getMapName(docId), count }, fallback: "later" });
    if (choice === "later" || cb.getCurrentDocId() !== docId) {
      later.add(docId);
      return;
    }
    if (choice === "copy") {
      try {
        const result = await cb.copyMap(docId, { title: t("sync.refused.copyName", { title: cb.getMapName(docId) }) });
        // Not stored as a map: keep the changes here (a copy not yet uploaded is stored).
        if (result?.errors?.length) throw new Error(`copy not made: ${result.errors.length} error(s)`);
      } catch (err) {
        console.error("[RefusedChanges] Copy failed; the changes stay on this device:", err);
        notify({ code: NOTICE.REFUSED_COPY_FAILED, level: "error", message: t("sync.refused.copyFailed"), data: { mapId: docId }, duration: 8000 });
        later.add(docId);
        return;
      }
    }
    await discardLocalChanges(docId);
  } finally {
    handling.delete(docId);
  }
}

/** The map as the server has it: this device's copy deleted, then opened again. */
async function discardLocalChanges(docId) {
  discarded.add(docId);
  await cb.stopSync(); // closes the copy before its saved state is deleted
  await deleteDocCache(docId);
  markConfirmed(docId); // nothing left here that the server lacks
  await cb.reopen(docId);
}
